import {
  Box,
  FormControl,
  FormHelperText,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";

interface INDateFormFieldProps {
  /** form element name */
  name: string;
  /** optional label text */
  label?: string;
  /** options for an optional 'ongoing' checkbox */
  /** on change event listener */
  onValueChange?: (value: Dayjs | null) => void;
  isDisabled?: boolean;
  /** The views to display by the MUI Datepicker. Default: ["day", "month", "year"] */
  views?: ("year" | "month" | "day")[];
  /** used to set the day to the last day of the month instead of the first, when using views: ["day", "month"] */
  setEndOfMonth?: boolean;
}

/** date picker */
export const INDateFormField: FC<INDateFormFieldProps> = ({
  name,
  label,
  onValueChange,
  isDisabled,
  views,
  setEndOfMonth = false,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = errors[name];

  return (
    <Box
      display="flex"
      sx={{ ".MuiFormControl-root": { width: "100%" } }}
      data-testid="DateFormField"
    >
      <FormControl sx={{ height: "100%" }} error={!!error}>
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value, ...field } }) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={isDisabled}
                views={views ?? ["day", "month", "year"]}
                PaperProps={{
                  sx: {
                    ".Mui-selected": {
                      color: "#ffffff !important",
                    },
                  },
                }}
                renderInput={(params: TextFieldProps) => (
                  <>
                    <TextField
                      {...params}
                      disabled
                      sx={{
                        fieldset: {
                          borderRadius: "5px",
                          borderColor: error ? "#d32f2f !important" : "inherit",
                        },
                      }}
                    />
                    {error && (
                      <FormHelperText>{error.message as string}</FormHelperText>
                    )}
                  </>
                )}
                label={label}
                {...field}
                onChange={(date: Dayjs | null) => {
                  if (!date) {
                    return;
                  }

                  if (!views?.includes("day")) {
                    date = date.startOf("month");

                    if (setEndOfMonth) {
                      date = date.endOf("month");
                    }
                  }

                  onChange(date.toISOString());
                  onValueChange?.(date);
                }}
                value={value ?? null}
              />
            </LocalizationProvider>
          )}
        />
      </FormControl>
    </Box>
  );
};
